import { go, mapL, some, tap } from "fxjs/es";
import { CommonNS } from "../../../../Common/CommonNS.js";
import { VONS } from "../../VO/VONS.js";
import { TransformManipulator } from "./TransformManipulator.js";
export class TranslateTransform {
  constructor({ svg_el, transform_manipulator }) {
    this._observer_set = new Set();
    this._svg_el = svg_el;
    this._transform_manipulator = transform_manipulator;
  }
  subscribe(observer) {
    if (this._observer_set.has(observer)) {
      throw new Error(`이미 등록된 observer 입니다.`);
    }
    this._observer_set.add(observer);
    return () => {
      this._observer_set.delete(observer);
    };
  }
  translateSingleElToViewPortTargetPoint({ el, view_port_target_point }) {
    this.translate({
      el,
      f: ({ el, svg_el }) => {
        const bbox = el.getBBox();
        const el_ctm = el.getCTM();
        if (el_ctm == null) {
          throw new Error(`엘리먼트의 CTM 을 구할 수 없습니다.`);
        }
        const [point1, point2] = go(
          [
            [bbox.x, bbox.y],
            [bbox.x + bbox.width, bbox.y],
            [bbox.x + bbox.width, bbox.y + bbox.height],
            [bbox.x, bbox.y + bbox.height],
          ],
          mapL(CommonNS.UtilNS.createSVGPoint(svg_el)),
          mapL((point) => point.matrixTransform(el_ctm)),
          CommonNS.UtilNS.calculateMinMaxXY,
          ({ min_x, min_y, max_x, max_y }) => [
            [min_x, min_y],
            [max_x, max_y],
          ],
          mapL(CommonNS.UtilNS.createSVGPoint(svg_el))
        );
        const center_point = CommonNS.UtilNS.createSVGPoint(svg_el)([
          (point1.x + point2.x) / 2,
          (point1.y + point2.y) / 2,
        ]);
        const tx = view_port_target_point.x - center_point.x;
        const ty = view_port_target_point.y - center_point.y;
        const translate_transform = svg_el.createSVGTransform();
        translate_transform.setTranslate(tx, ty);
        const { matrix: translate_transform_matrix } = translate_transform;
        const el_fixed_transform_matrix =
          CommonNS.UtilNS.getConsolidatedTransformMatrix(el) ??
          svg_el.createSVGMatrix();
        return el_fixed_transform_matrix
          .multiply(el_ctm.inverse())
          .multiply(translate_transform_matrix)
          .multiply(el_ctm)
          .multiply(el_fixed_transform_matrix.inverse());
      },
    });
  }
  translateSingleElAlignTo({ el, align, coordinate }) {
    this.translate({
      el,
      f: ({ el, svg_el }) => {
        const bbox = el.getBBox();
        const el_ctm = el.getCTM();
        if (el_ctm == null) {
          throw new Error(`엘리먼트의 CTM 을 구할 수 없습니다.`);
        }
        const el_fixed_transform =
          this._transform_manipulator.getFixedTransform(el);
        if (el_fixed_transform == null) {
          throw new Error(`엘리먼트의 fixed transform 을 구할 수 없습니다.`);
        }
        const { matrix: el_fixed_transform_matrix } = el_fixed_transform;
        const { min_x, min_y, max_x, max_y } = go(
          [
            [bbox.x, bbox.y],
            [bbox.x + bbox.width, bbox.y],
            [bbox.x + bbox.width, bbox.y + bbox.height],
            [bbox.x, bbox.y + bbox.height],
          ],
          mapL(CommonNS.UtilNS.createSVGPoint(svg_el)),
          mapL((point) => point.matrixTransform(el_fixed_transform_matrix)),
          CommonNS.UtilNS.calculateMinMaxXY,
          ({ min_x, min_y, max_x, max_y }) => [
            [min_x, min_y],
            [max_x, min_y],
            [max_x, max_y],
            [min_x, max_y],
          ],
          mapL(CommonNS.UtilNS.createSVGPoint(svg_el)),
          mapL((point) =>
            point.matrixTransform(
              el_ctm.multiply(el_fixed_transform_matrix.inverse())
            )
          ),
          CommonNS.UtilNS.calculateMinMaxXY,
          tap(({ min_x, min_y, max_x, max_y }) => {
            if (
              some((a) => Number.isNaN(a) || !Number.isFinite(a))([
                min_x,
                min_y,
                max_x,
                max_y,
              ])
            ) {
              throw new Error(`엘리먼트의 bounding box 를 계산할 수 없습니다.`);
            }
          })
        );
        const { tx, ty } = (() => {
          switch (align) {
            case "top": {
              return {
                tx: 0,
                ty: coordinate - min_y,
              };
            }
            case "right": {
              return {
                tx: coordinate - max_x,
                ty: 0,
              };
            }
            case "bottom": {
              return {
                tx: 0,
                ty: coordinate - max_y,
              };
            }
            case "left": {
              return {
                tx: coordinate - min_x,
                ty: 0,
              };
            }
            case "horizontal_center": {
              return {
                tx: coordinate - (min_x + max_x) / 2,
                ty: 0,
              };
            }
            case "vertical_center": {
              return {
                tx: 0,
                ty: coordinate - (min_y + max_y) / 2,
              };
            }
            default: {
              throw new Error(`올바르지 않은 align 값입니다.`);
            }
          }
        })();
        const translate_transform = svg_el.createSVGTransform();
        translate_transform.setTranslate(tx, ty);
        const { matrix: translate_transform_matrix } = translate_transform;
        const el_fixed_transform_matrix2 =
          CommonNS.UtilNS.getConsolidatedTransformMatrix(el) ??
          svg_el.createSVGMatrix();
        return el_fixed_transform_matrix2
          .multiply(el_ctm.inverse())
          .multiply(translate_transform_matrix)
          .multiply(el_ctm)
          .multiply(el_fixed_transform_matrix2.inverse());
      },
    });
  }
  translate({ el, f }) {
    this._transform_manipulator.initTransform(el);
    const matrix = f({
      svg_el: this._svg_el,
      el,
    });
    this._transform_manipulator.setExtendedTransformAfterBase(matrix, el);
    this._transform_manipulator.mergeExtendedTransformToBase(el);
    this._notify({
      el,
    });
    el.dispatchEvent(
      new CustomEvent(`@mp/sticker-editor/transform`, {
        bubbles: true,
        cancelable: true,
        composed: false,
        detail: {
          el,
          transform_type: "translate",
          transform_status: "end",
        },
      })
    );
  }
  _notify(transform_vo) {
    for (const observer of this._observer_set) {
      CommonNS.UtilNS.tryNoop(() => observer(transform_vo));
    }
  }
}
